import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    showDescription = "showDescription",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDescription]: boolean;
    };
}

type Props = WidgetProps & HasProduct & OwnProps;

export interface NISPdpDescriptionStyles {
    attributeTypeValuesText?: TypographyPresentationProps;
    container?: InjectableCss;
    titleStyle?: InjectableCss;
    linkStyles?: InjectableCss;
}

export const descriptionStyles: NISPdpDescriptionStyles = {
    linkStyles: {
        css: css`
            display: block;
            margin-top: 4px;
            &:focus {
                outline-color: transparent !important;
            }
            > span {
                color: #000000 !important;
                font-size: 16px !important;
                font-weight: 600;
                line-height: 24px;
                font-family: Roboto;
            }
        `,
    },
    attributeTypeValuesText: {
        css: css`
            font-family: Roboto !important;
            font-weight: 400 !important;
            font-size: 16px !important;
            line-height: 24px !important;
            color: #444444 !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 3 !important; /* 👈 limit to 3 lines */
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            p {
                font-family: Roboto !important;
                font-weight: 400 !important;
                font-size: 16px !important;
                line-height: 24px !important;
                color: #444444 !important;
            }
        `,
    },
    container: {
        css: css`
            width: 100%;
            // height: 100%;
            margin: 24px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding-right: 104px;
                        `,
                        css`
                            padding-right: 104px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    titleStyle: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: #000;
            margin: 25px 0px;
        `,
    },
};

const styles = descriptionStyles;

const NISPdpDescription: React.FC<Props> = ({
    product: { content, properties, specifications },
    fields: { showDescription },
}) => {
    const description = content?.htmlContent ? content?.htmlContent : properties.brandHtmlContent;
    if (!description) {
        return null;
    }
    const renderingDescription = description.split("</head>");
    const contentLength = renderingDescription?.length > 1 ? renderingDescription[1].length : description?.length;
    const [status, setStatus] = useState(false);
    const toggleStatus = () => {
        setStatus(!status);
    };
    if (!renderingDescription) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.container}>
                {/* {renderingDescription && showDescription && (
                    <StyledWrapper key="Overview">
                        <Typography {...styles.attributeTypeValuesText}>
                            {parse(transformContentHtml(description), parserOptions)}
                        </Typography>
                    </StyledWrapper>
                )} */}
                {renderingDescription && showDescription && (
                    <StyledWrapper key="Overview">
                        <StyledWrapper>
                            <Typography
                                {...styles.attributeTypeValuesText}
                                css={css`
                                    display: -webkit-box !important;
                                    -webkit-line-clamp: ${status ? "unset" : 3};
                                    -webkit-box-orient: vertical;
                                    overflow: hidden;
                                    text-overflow: ellipsis;

                                    /* Force children inline for clamping */
                                    p,
                                    div,
                                    span {
                                        display: inline !important;
                                        font-family: Roboto !important;
                                        font-size: 16px !important;
                                        line-height: 24px !important;
                                        color: #444444 !important;
                                    }
                                `}
                            >
                                {parse(transformContentHtml(description), parserOptions)}
                            </Typography>

                            {contentLength > 0 && !getIsWebCrawler() && (
                                <Link
                                    href=""
                                    onClick={e => {
                                        e.preventDefault();
                                        toggleStatus();
                                    }}
                                    {...styles.linkStyles}
                                >
                                    {status ? "See less" : "See more"}
                                </Link>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(null)(NISPdpDescription)),
    definition: {
        displayName: "NIS Pdp Description",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: fields.showDescription,
                displayName: "Show Description",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
